import type {
  Conversation,
  Message,
  AttentionItem,
  User,
  Studio,
  UserRole,
  StudioRole,
  MessageProvenance,
  ChatHandlingContext,
  AutoSendCoverage,
  ConversationGroupBookingDraft,
  GroupBookingPolicyMode,
  ConversationMessagingSession,
  FirstTrialLinksBlockReason,
  EmailAttachment,
  EmailDeliveryStatus,
} from "@/types";
import { normalizeStudioBookingSource } from "@/lib/studio-data-filters";
import { normalizeAccountVertical } from "@/lib/vertical";
import { isSupportedUiLocale } from "@/lib/i18n/constants";
import { parseUnreadCount } from "@/lib/unread-count";

const intentMap: Record<string, Conversation["intent"]> = {
  booking: "Booking",
  rebooking: "Rebook",
  cancellation: "Cancel",
  info: "Info",
  schedule: "Schedule",
  account: "Account",
  other: "Other",
};

const channelMap: Record<string, Conversation["channel"]> = {
  instagram_dm: "instagram",
  whatsapp: "whatsapp",
  facebook: "facebook",
  email: "email",
};

function mapEmailAttachments(raw: unknown): EmailAttachment[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const items = raw
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const o = entry as Record<string, unknown>;
      const id = typeof o.id === "string" ? o.id.trim() : "";
      const filename =
        typeof o.filename === "string"
          ? o.filename.trim()
          : typeof o.name === "string"
            ? o.name.trim()
            : "";
      if (!id || !filename) return null;
      const mimeType =
        typeof o.mimeType === "string"
          ? o.mimeType
          : typeof o.mime_type === "string"
            ? o.mime_type
            : undefined;
      const sizeBytes =
        typeof o.sizeBytes === "number"
          ? o.sizeBytes
          : typeof o.size_bytes === "number"
            ? o.size_bytes
            : undefined;
      const contentId =
        typeof o.contentId === "string"
          ? o.contentId.trim()
          : typeof o.content_id === "string"
            ? o.content_id.trim()
            : undefined;
      const url =
        typeof o.url === "string"
          ? o.url.trim()
          : typeof o.downloadUrl === "string"
            ? o.downloadUrl.trim()
            : typeof o.download_url === "string"
              ? o.download_url.trim()
              : undefined;
      return {
        id,
        filename,
        ...(mimeType ? { mimeType } : {}),
        ...(sizeBytes != null ? { sizeBytes } : {}),
        ...(contentId ? { contentId } : {}),
        ...(url ? { url } : {}),
      };
    })
    .filter((x): x is EmailAttachment => x !== null);
  return items.length > 0 ? items : undefined;
}

function mapEmailDeliveryStatus(raw: unknown): EmailDeliveryStatus | undefined {
  if (raw === "delivered" || raw === "failed" || raw === "pending") return raw;
  return undefined;
}

function readString(record: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function readStepIndex(details: Record<string, unknown>): number | null {
  const stepIndexRaw = details.stepIndex ?? details.step_index;
  if (typeof stepIndexRaw === "number" && Number.isFinite(stepIndexRaw)) return stepIndexRaw;
  if (typeof stepIndexRaw === "string" && /^\d+$/.test(stepIndexRaw.trim())) return Number(stepIndexRaw.trim());
  return null;
}

function parseActionLogWorkflow(details: Record<string, unknown>): {
  goal?: string;
  tier?: string;
  step?: number;
  stepDescription?: string;
} | null {
  const goal = readString(details, "goal", "goalName", "goal_name");
  const tier = readString(details, "tier", "tierName", "tier_name", "executionTier", "execution_tier");
  const stepDescription = readString(details, "stepDescription", "step_description", "description");
  const stepIndex = readStepIndex(details);
  if (!goal && !tier && stepIndex == null && !stepDescription) return null;
  return {
    goal: goal ?? undefined,
    tier: tier ?? undefined,
    step: stepIndex ?? undefined,
    stepDescription: stepDescription ?? undefined,
  };
}

function formatActionLogExecutionMeta(details: Record<string, unknown>): string[] {
  const fragments: string[] = [];
  const workflow = parseActionLogWorkflow(details);
  if (workflow?.goal) fragments.push(`goal: ${workflow.goal}`);
  if (workflow?.tier) fragments.push(`tier: ${workflow.tier}`);
  if (workflow?.stepDescription && workflow.step != null) fragments.push(`step ${workflow.step}: ${workflow.stepDescription}`);
  else if (workflow?.stepDescription) fragments.push(`step: ${workflow.stepDescription}`);
  else if (workflow?.step != null) fragments.push(`step: ${workflow.step}`);

  if (fragments.length > 0) return fragments;
  const steps = details.steps;
  if (Array.isArray(steps)) {
    const labels = steps.filter((entry): entry is string => typeof entry === "string" && entry.trim()).slice(0, 3);
    if (labels.length > 0) fragments.push(`steps: ${labels.join(" | ")}`);
  }
  return fragments;
}

function mapActionLogEntry(entry: any): {
  icon: string;
  text: string;
  timestamp: string;
  workflow?: {
    goal?: string;
    tier?: string;
    step?: number;
    stepDescription?: string;
  };
} {
  const actionLabels: Record<string, string> = {
    draft_approved: "Draft approved and sent",
    draft_edited: "Draft edited",
    draft_edited_and_sent: "Draft edited and sent",
    draft_regenerated: "Draft regenerated",
    manual_reply_sent: "Manual reply sent",
    whatsapp_template_sent: "WhatsApp template sent",
    auto_reply_sent: "Auto-reply sent",
    status_changed: "Status changed",
    faq_created: "FAQ created",
    faq_edited: "FAQ edited",
    faq_deleted: "FAQ deleted",
    toggle_changed: "Setting changed",
  };
  const label = actionLabels[entry.actionType] ?? entry.actionType;
  const by = entry.userName ? ` by ${entry.userName}` : "";
  let detailsText = typeof entry.details === "string" && entry.details.trim() ? entry.details.trim() : "";
  const detailsMeta =
    entry.details && typeof entry.details === "object"
      ? formatActionLogExecutionMeta(entry.details as Record<string, unknown>)
      : [];
  const workflow =
    entry.details && typeof entry.details === "object"
      ? parseActionLogWorkflow(entry.details as Record<string, unknown>) ?? undefined
      : undefined;
  if (!detailsText && entry.actionType === "auto_reply_sent" && entry.details && typeof entry.details === "object") {
    const d = entry.details as { intent?: string; confidence?: number; responsePreview?: string };
    const parts: string[] = [];
    if (d.intent) parts.push(`intent: ${d.intent}`);
    if (typeof d.confidence === "number") parts.push(`${Math.round(d.confidence * 100)}% confidence`);
    if (d.responsePreview) {
      const prev = String(d.responsePreview);
      parts.push(`"${prev.slice(0, 80)}${prev.length > 80 ? "…" : ""}"`);
    }
    detailsText = parts.length > 0 ? parts.join(" · ") : "";
  }
  const detailsWithMeta =
    detailsMeta.length > 0
      ? [detailsText, ...detailsMeta].filter((part) => part.trim().length > 0).join(" · ")
      : detailsText;
  const text = detailsWithMeta || `${label}${by}`;
  const icon = entry.actionType === "status_changed" ? "warning" : "check";
  const ts = entry.timestamp ? new Date(entry.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  return { icon, text, timestamp: ts, workflow };
}

function mapIdentificationStatus(raw: Record<string, unknown>): Conversation["identificationStatus"] {
  const approvedRaw = raw.identityApprovedAt ?? raw.identity_approved_at;
  if (approvedRaw != null && String(approvedRaw).trim() !== "") {
    const t =
      approvedRaw instanceof Date ? approvedRaw.getTime() : new Date(String(approvedRaw)).getTime();
    if (!Number.isNaN(t)) return "identified";
  }
  const v = raw.identificationStatus ?? raw.identification_status;
  if (v === "identified" || v === "id_pending" || v === "unmatched" || v === "not_required") return v;
  return "id_pending";
}

function mapMessageProvenance(v: unknown): MessageProvenance | undefined {
  if (v === "customer" || v === "ai_orchestrator_auto" || v === "ai_draft_approved" || v === "team" || v === "session_nudge") return v;
  return undefined;
}

function mapAutoSendCoverage(v: unknown): AutoSendCoverage | undefined {
  if (v === "all" || v === "none" || v === "mixed") return v;
  return undefined;
}

function mapGroupBookingModeApplied(v: unknown): GroupBookingPolicyMode | undefined {
  if (v === "off" || v === "human_first" || v === "assistive_draft") return v;
  return undefined;
}

/** Normalize `groupBooking` from draft / proposed_action (handover §6.5). */
export function mapGroupBookingPayload(raw: unknown): ConversationGroupBookingDraft | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  if (o.active !== true) return undefined;
  const modeApplied = mapGroupBookingModeApplied(o.modeApplied ?? o.mode_applied);
  const signals = Array.isArray(o.signals)
    ? o.signals.filter((s): s is string => typeof s === "string")
    : undefined;
  const extractedPartySize =
    typeof o.extractedPartySize === "number"
      ? o.extractedPartySize
      : typeof o.extracted_party_size === "number"
        ? o.extracted_party_size
        : undefined;
  let ui: ConversationGroupBookingDraft["ui"] | undefined;
  const uiRaw = o.ui;
  if (uiRaw && typeof uiRaw === "object") {
    const u = uiRaw as Record<string, unknown>;
    const keys = u.checklistKeys ?? u.checklist_keys;
    const checklistKeys = Array.isArray(keys) ? keys.filter((k): k is string => typeof k === "string") : [];
    ui = {
      checklistKeys,
      overrideReasonKey:
        typeof u.overrideReasonKey === "string"
          ? u.overrideReasonKey
          : typeof u.override_reason_key === "string"
            ? u.override_reason_key
            : undefined,
    };
  }
  return { active: true, modeApplied, signals, extractedPartySize, ui };
}

function proposedActionDescription(pa: unknown): string {
  if (pa == null || pa === "") return "";
  if (typeof pa === "string") return pa;
  if (typeof pa === "object") {
    const d = (pa as Record<string, unknown>).description;
    if (typeof d === "string") return d;
  }
  return "";
}

function mapFirstTrialLinksBlockReason(v: unknown): FirstTrialLinksBlockReason | undefined {
  if (v === "missing_trial_registration_link" || v === "missing_studio_trial_booking_link") return v;
  return undefined;
}

function readFirstTrialLinksNotReady(...records: Array<Record<string, unknown> | null>): boolean {
  return records.some((record) => record?.firstTrialLinksNotReady === true || record?.first_trial_links_not_ready === true);
}

export function mapConversationMessagingSession(raw: unknown): ConversationMessagingSession | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const health = o.health;
  if (health !== "open" && health !== "expiring_soon" && health !== "expired" && health !== "unknown") return undefined;
  const tr = o.timeRemainingSec ?? o.time_remaining_sec;
  let timeRemainingSec: number | null = null;
  if (typeof tr === "number" && Number.isFinite(tr)) timeRemainingSec = tr;
  else if (typeof tr === "string" && tr.trim() !== "") {
    const n = parseInt(tr, 10);
    timeRemainingSec = Number.isNaN(n) ? null : n;
  }
  const windowEndsAt =
    typeof o.windowEndsAt === "string"
      ? o.windowEndsAt
      : typeof o.window_ends_at === "string"
        ? o.window_ends_at
        : null;
  const unanswered = o.unanswered;
  const nudgeLastSentAt =
    typeof o.nudgeLastSentAt === "string"
      ? o.nudgeLastSentAt
      : typeof o.nudge_last_sent_at === "string"
        ? o.nudge_last_sent_at
        : undefined;
  return {
    windowEndsAt: windowEndsAt ?? null,
    health,
    timeRemainingSec,
    ...(typeof unanswered === "boolean" ? { unanswered } : {}),
    ...(nudgeLastSentAt ? { nudgeLastSentAt } : {}),
  };
}

export function mapConversation(raw: any): Conversation {
  const customer = (raw.customer ?? {}) as Record<string, unknown>;
  const upcomingClassesRaw = (raw.upcomingClasses ??
    raw.upcoming_classes ??
    customer.upcomingClasses ??
    customer.upcoming_classes ??
    []) as unknown;
  const upcomingClasses = Array.isArray(upcomingClassesRaw)
    ? upcomingClassesRaw
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object")
        .map((entry) => ({
          id: typeof entry.id === "string" ? entry.id : undefined,
          startsAt:
            typeof entry.startsAt === "string"
              ? entry.startsAt
              : typeof entry.starts_at === "string"
                ? entry.starts_at
                : typeof entry.time === "string"
                  ? entry.time
                  : undefined,
          name:
            typeof entry.name === "string"
              ? entry.name
              : typeof entry.className === "string"
                ? entry.className
                : typeof entry.class_name === "string"
                  ? entry.class_name
                  : undefined,
          instructor:
            typeof entry.instructor === "string"
              ? entry.instructor
              : typeof entry.trainer === "string"
                ? entry.trainer
                : undefined,
        }))
    : undefined;
  const documentsRaw = (raw.documents ??
    raw.attachments ??
    raw.files ??
    customer.documents ??
    customer.attachments ??
    []) as unknown;
  const documents = Array.isArray(documentsRaw)
    ? documentsRaw
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object")
        .map((entry) => {
          const name =
            typeof entry.name === "string"
              ? entry.name
              : typeof entry.fileName === "string"
                ? entry.fileName
                : typeof entry.file_name === "string"
                  ? entry.file_name
                  : typeof entry.filename === "string"
                    ? entry.filename
                    : "";
          if (!name.trim()) return null;
          const senderRaw =
            typeof entry.sender === "string"
              ? entry.sender
              : typeof entry.source === "string"
                ? entry.source
                : undefined;
          const sender: "customer" | "staff" | undefined =
            senderRaw === "customer" ? "customer" : senderRaw === "staff" ? "staff" : undefined;
          return {
            id: typeof entry.id === "string" ? entry.id : undefined,
            name,
            mimeType:
              typeof entry.mimeType === "string"
                ? entry.mimeType
                : typeof entry.mime_type === "string"
                  ? entry.mime_type
                  : typeof entry.contentType === "string"
                    ? entry.contentType
                    : undefined,
            url:
              typeof entry.url === "string"
                ? entry.url
                : typeof entry.href === "string"
                  ? entry.href
                  : undefined,
            sender,
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    : undefined;
  const actionLog = Array.isArray(raw.actionLog) ? raw.actionLog.map(mapActionLogEntry) : undefined;
  const handlingContext = raw.handlingContext as ChatHandlingContext | undefined;
  return {
    id: raw.id,
    customerProfileId:
      raw.customerProfileId != null && String(raw.customerProfileId).trim() !== ""
        ? String(raw.customerProfileId).trim()
        : raw.customer_profile_id != null && String(raw.customer_profile_id).trim() !== ""
          ? String(raw.customer_profile_id).trim()
          : null,
    customerId:
      raw.customerId != null && String(raw.customerId).trim() !== ""
        ? String(raw.customerId).trim()
        : raw.customer_id != null && String(raw.customer_id).trim() !== ""
          ? String(raw.customer_id).trim()
          : undefined,
    channelSenderId:
      typeof raw.channelSenderId === "string"
        ? raw.channelSenderId
        : typeof raw.channel_sender_id === "string"
          ? raw.channel_sender_id
          : undefined,
    customerName: raw.customerName || raw.customer?.name || "Unknown",
    customerAvatar: raw.customerAvatarUrl || raw.customer?.avatar || undefined,
    customerPhone:
      typeof raw.customerPhone === "string"
        ? raw.customerPhone
        : typeof raw.customer_phone === "string"
          ? raw.customer_phone
          : typeof customer.phone === "string"
            ? customer.phone
            : undefined,
    customerInstagram:
      typeof raw.customerInstagram === "string"
        ? raw.customerInstagram
        : typeof raw.customer_instagram === "string"
          ? raw.customer_instagram
          : typeof customer.instagram === "string"
            ? customer.instagram
            : typeof customer.instagramHandle === "string"
              ? customer.instagramHandle
              : undefined,
    customerEmail:
      typeof raw.customerEmail === "string"
        ? raw.customerEmail
        : typeof raw.customer_email === "string"
          ? raw.customer_email
          : typeof customer.email === "string"
            ? customer.email
            : undefined,
    customerSecondaryId:
      typeof raw.customerSecondaryId === "string"
        ? raw.customerSecondaryId
        : typeof raw.customer_secondary_id === "string"
          ? raw.customer_secondary_id
          : typeof raw.secondaryExternalId === "string"
            ? raw.secondaryExternalId
            : typeof raw.secondary_external_id === "string"
              ? raw.secondary_external_id
              : typeof customer.secondaryId === "string"
                ? customer.secondaryId
                : typeof customer.secondary_id === "string"
                  ? customer.secondary_id
                  : typeof customer.secondaryExternalId === "string"
                    ? customer.secondaryExternalId
                    : typeof customer.secondary_external_id === "string"
                      ? customer.secondary_external_id
                      : undefined,
    upcomingClasses,
    documents: documents && documents.length > 0 ? documents : undefined,
    channel: channelMap[raw.channel] || "whatsapp",
    subject: typeof raw.subject === "string" && raw.subject.trim() ? raw.subject.trim() : undefined,
    studioId: raw.studioId || raw.studio_id || "",
    studioName: raw.studioName || raw.studio_name || "",
    intent: intentMap[raw.intentType || raw.intent] || "Other",
    status: raw.status,
    lastMessage: raw.lastMessagePreview || raw.lastMessage || "",
    timestamp: raw.lastActivityAt || raw.timestamp || raw.createdAt || "",
    unreadCount: parseUnreadCount(raw),
    unread: raw.hasUnread === true || raw.has_unread === true || !!raw.unread || parseUnreadCount(raw) > 0,
    identificationStatus: mapIdentificationStatus(raw),
    identityApprovedAt: (() => {
      const a = raw.identityApprovedAt ?? raw.identity_approved_at;
      if (a == null) return undefined;
      if (a instanceof Date && !Number.isNaN(a.getTime())) return a.toISOString();
      const s = String(a).trim();
      if (!s) return undefined;
      const t = new Date(s).getTime();
      return Number.isNaN(t) ? undefined : new Date(t).toISOString();
    })(),
    autoHandle: raw.status === "ai_handled",
    ...(raw.groupBookingActive === true || raw.group_booking_active === true ? { groupBookingActive: true as const } : {}),
    draftData: (() => {
      const aiDraftRaw = raw.aiDraft ?? raw.ai_draft;
      if (!aiDraftRaw || typeof aiDraftRaw !== "object") return undefined;
      const aiDraft = aiDraftRaw as Record<string, unknown>;
      const proposedActionRaw = aiDraft.proposedAction ?? aiDraft.proposed_action;
      const paObj =
        proposedActionRaw !== null && typeof proposedActionRaw === "object"
          ? (proposedActionRaw as Record<string, unknown>)
          : null;
      const gbFromPa =
        paObj && "groupBooking" in paObj
          ? paObj.groupBooking
          : paObj && "group_booking" in paObj
            ? paObj.group_booking
            : undefined;
      const gbTop = aiDraft.groupBooking ?? aiDraft.group_booking;
      const groupBooking = mapGroupBookingPayload(gbFromPa ?? gbTop);
      const firstTrialFromPa =
        paObj?.firstTrialScenario === "no_platform_account" || paObj?.firstTrialScenario === "has_platform_account"
          ? paObj.firstTrialScenario
          : undefined;
      const firstTrialFromDraft =
        aiDraft.firstTrialScenario === "no_platform_account" || aiDraft.firstTrialScenario === "has_platform_account"
          ? aiDraft.firstTrialScenario
          : undefined;
      const firstTrialLinksBlockReason =
        mapFirstTrialLinksBlockReason(paObj?.firstTrialLinksBlockReason) ??
        mapFirstTrialLinksBlockReason(paObj?.first_trial_links_block_reason) ??
        mapFirstTrialLinksBlockReason(aiDraft.firstTrialLinksBlockReason) ??
        mapFirstTrialLinksBlockReason(aiDraft.first_trial_links_block_reason);
      const firstTrialLinksNotReady = readFirstTrialLinksNotReady(paObj, aiDraft);
      const draftIdRaw = aiDraft.id;
      return {
        ...(typeof draftIdRaw === "string" && draftIdRaw.trim() ? { id: draftIdRaw } : {}),
        content: String(aiDraft.proposedMessage ?? aiDraft.text ?? aiDraft.proposed_message ?? ""),
        proposedAction: proposedActionDescription(proposedActionRaw),
        ...(firstTrialFromDraft ?? firstTrialFromPa
          ? { firstTrialScenario: firstTrialFromDraft ?? firstTrialFromPa }
          : {}),
        ...(firstTrialLinksNotReady ? { firstTrialLinksNotReady: true } : {}),
        ...(firstTrialLinksBlockReason ? { firstTrialLinksBlockReason } : {}),
        blocked: typeof aiDraft.blocked === "boolean" ? aiDraft.blocked : false,
        blockedReason:
          typeof aiDraft.blockedReason === "string"
            ? aiDraft.blockedReason
            : typeof aiDraft.blocked_reason === "string"
              ? aiDraft.blocked_reason
              : undefined,
        groupBooking,
      };
    })(),
    actionLog,
    handlingContext:
      handlingContext &&
      typeof handlingContext === "object" &&
      typeof handlingContext.draftMode === "boolean" &&
      typeof handlingContext.automatedReplyCustomerAttribution === "string"
        ? ({
            ...handlingContext,
            autoSendCoverage:
              mapAutoSendCoverage((handlingContext as ChatHandlingContext).autoSendCoverage) ??
              ((handlingContext as ChatHandlingContext).draftMode ? "mixed" : "all"),
          } as ChatHandlingContext)
        : undefined,
    ...(() => {
      const session = mapConversationMessagingSession(raw.session);
      return session ? { session } : {};
    })(),
  };
}

function looksLikeHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*?>/i.test(text);
}

export function mapMessage(raw: any, conversationId: string): Message {
  const apiSender = String(raw.sender ?? "");
  const direction = String(raw.direction ?? "");
  const bodyHtmlEarly =
    typeof raw.bodyHtml === "string"
      ? raw.bodyHtml
      : typeof raw.body_html === "string"
        ? raw.body_html
        : undefined;
  const isEmailShape = Boolean(bodyHtmlEarly || (typeof raw.from === "string" && raw.from.trim()));
  const sender: Message["sender"] =
    apiSender === "customer" || (isEmailShape && direction === "inbound")
      ? "customer"
      : apiSender === "ai"
        ? "ai"
        : apiSender === "system"
          ? "system"
          : "human";
  const sn = raw.senderName ?? raw.sender_name;
  const senderLabel = typeof sn === "string" && sn.trim() ? sn.trim() : undefined;
  const mp = raw.messageProvenance ?? raw.message_provenance;
  const rawContent = typeof raw.content === "string" ? raw.content.trim() : "";
  let bodyHtml = bodyHtmlEarly;
  if (!bodyHtml && isEmailShape && rawContent && looksLikeHtml(rawContent)) {
    bodyHtml = rawContent;
  }
  const bodyText =
    typeof raw.bodyText === "string"
      ? raw.bodyText
      : typeof raw.body_text === "string"
        ? raw.body_text
        : undefined;
  const from = typeof raw.from === "string" && raw.from.trim() ? raw.from.trim() : undefined;
  const toRaw = raw.to;
  const to = Array.isArray(toRaw) ? toRaw.filter((x): x is string => typeof x === "string" && x.trim().length > 0) : undefined;
  const ccRaw = raw.cc;
  const cc = Array.isArray(ccRaw) ? ccRaw.filter((x): x is string => typeof x === "string" && x.trim().length > 0) : undefined;
  const subject = typeof raw.subject === "string" && raw.subject.trim() ? raw.subject.trim() : undefined;
  const attachments = mapEmailAttachments(raw.attachments);
  const deliveryStatus =
    mapEmailDeliveryStatus(raw.deliveryStatus ?? raw.delivery_status) ??
    (raw.status === "failed" ? ("failed" as const) : undefined);
  return {
    id: raw.id,
    conversationId,
    sender,
    senderLabel,
    messageProvenance: mapMessageProvenance(mp),
    content: bodyText || raw.content || raw.text || "",
    timestamp: raw.sentAt || raw.sent_at || raw.timestamp || "",
    isEdited: raw.isEdited === true || raw.is_edited === true ? true : undefined,
    editedAt:
      typeof (raw.editedAt ?? raw.edited_at) === "string"
        ? (raw.editedAt ?? raw.edited_at)
        : undefined,
    ...(from ? { from } : {}),
    ...(to && to.length > 0 ? { to } : {}),
    ...(cc && cc.length > 0 ? { cc } : {}),
    ...(subject ? { subject } : {}),
    ...(bodyHtml ? { bodyHtml } : {}),
    ...(bodyText ? { bodyText } : {}),
    ...(deliveryStatus ? { deliveryStatus } : {}),
    ...(attachments ? { attachments } : {}),
  };
}

export function mapAttentionItem(raw: any): AttentionItem {
  const typ = String(raw.type ?? "");
  const type: AttentionItem["type"] =
    typ === "escalated" ? "escalated" : typ === "session_expiring" ? "session_expiring" : "draft_ready";
  return {
    id: raw.conversationId || raw.id || "",
    type,
    conversationId: raw.conversationId || raw.id || "",
    customerName: raw.customerName || "Unknown",
    channel: channelMap[raw.channel] || "whatsapp",
    intent: intentMap[raw.intentType || raw.intent] || "Other",
    messagePreview: raw.previewText || raw.lastMessagePreview || raw.lastMessage || "",
    studioName: raw.studioName || "",
    timestamp: raw.waitingSince || raw.lastActivityAt || raw.timestamp || "",
    actionSummary: typeof raw.actionSummary === "string" && raw.actionSummary.length > 0 ? raw.actionSummary : undefined,
    studioId: typeof raw.studioId === "string" && raw.studioId.trim() ? raw.studioId.trim() : undefined,
    sessionWindowEndsAt: typeof raw.sessionWindowEndsAt === "string" ? raw.sessionWindowEndsAt : undefined,
  };
}

function mapConnectionStatus(v: string): { status: "connected" | "error" | "not_connected" } {
  if (v === "connected") return { status: "connected" };
  if (v === "error") return { status: "error" };
  return { status: "not_connected" };
}

function roleLabelFromStudioList(r: string | undefined): UserRole {
  const x = String(r ?? "staff").toLowerCase();
  if (x === "owner") return "Owner";
  if (x === "manager") return "Manager";
  return "Staff";
}

/** Map GET /studios response to Studio[], roles per studio, and currentStudioId. `studioRoles` must replace client state on refresh so deleted studios do not leave stale Owner/Manager rows. */
export function mapStudioListResponse(data: any): {
  studios: Studio[];
  studioRoles: StudioRole[];
  currentStudioId: string | "all" | null;
} {
  const list = data?.studios ?? [];
  const studios: Studio[] = list.map((s: any) => {
    const conn = s.connections ?? {};
    return {
      id: s.id,
      name: s.name,
      ownerUserId:
        (typeof s.ownerUserId === "string" && s.ownerUserId.trim().length > 0
          ? s.ownerUserId
          : typeof s.owner_user_id === "string" && s.owner_user_id.trim().length > 0
            ? s.owner_user_id
            : undefined),
      description: s.description ?? undefined,
      address: typeof s.address === "string" && s.address.trim() ? s.address.trim() : undefined,
      bookingSourceKey: normalizeStudioBookingSource(typeof s.source === "string" ? s.source : null),
      onboardingComplete: s.onboardingComplete ?? true,
      connections: {
        instagram: mapConnectionStatus(conn.instagram ?? "disconnected"),
        whatsapp: mapConnectionStatus(conn.whatsapp ?? "disconnected"),
        bookingPlatform: mapConnectionStatus(conn.bookingPlatform ?? "disconnected"),
      },
    };
  });
  const studioRoles: StudioRole[] = list
    .filter((s: any) => s && typeof s.id === "string" && s.id.length > 0)
    .map((s: any) => ({
      studioId: String(s.id),
      role: roleLabelFromStudioList(typeof s.role === "string" ? s.role : undefined),
    }));
  const currentStudioId = data?.currentStudioId ?? null;
  return { studios, studioRoles, currentStudioId };
}

export function mapLoginResponseToUser(data: any): User {
  const u = data?.user;
  if (!u || typeof u !== "object") {
    throw new Error("Invalid user payload");
  }

  const rawStudios = Array.isArray(u.studios) ? u.studios : [];
  const studios: Studio[] = rawStudios.map((s: any) => {
    const conn = s.connections ?? {};
    return {
      id: s.id,
      name: s.name,
      ownerUserId:
        (typeof s.ownerUserId === "string" && s.ownerUserId.trim().length > 0
          ? s.ownerUserId
          : typeof s.owner_user_id === "string" && s.owner_user_id.trim().length > 0
            ? s.owner_user_id
            : undefined),
      description: s.description ?? "",
      address: typeof s.address === "string" && s.address.trim() ? s.address.trim() : undefined,
      bookingSourceKey: normalizeStudioBookingSource(typeof s.source === "string" ? s.source : null),
      onboardingComplete: s.onboardingComplete ?? true,
      connections: {
        instagram: mapConnectionStatus(conn.instagram ?? "disconnected"),
        whatsapp: mapConnectionStatus(conn.whatsapp ?? "disconnected"),
        bookingPlatform: mapConnectionStatus(conn.bookingPlatform ?? "disconnected"),
      },
    };
  });

  const roleLabel = (r: string): UserRole => {
    const x = String(r).toLowerCase();
    if (x === "owner") return "Owner";
    if (x === "manager") return "Manager";
    return "Staff";
  };

  const studioRoles =
    Array.isArray(u.studioRoles) && u.studioRoles.length > 0
      ? u.studioRoles
          .filter((sr: any) => sr && typeof sr.studioId === "string")
          .map((sr: any) => ({
            studioId: sr.studioId,
            role: roleLabel(typeof sr.role === "string" ? sr.role : "staff"),
          }))
      : rawStudios.map((s: any) => ({
          studioId: String(s.id ?? ""),
          role: roleLabel(typeof s.role === "string" ? s.role : "owner"),
        }));

  const email = typeof u.email === "string" ? u.email : "";
  const id = u.id != null && String(u.id).length > 0 ? String(u.id) : "";
  if (!id) {
    throw new Error("Invalid user payload");
  }

  const nameFromEmail = email.includes("@") ? email.split("@")[0] : "User";
  const name =
    typeof u.name === "string" && u.name.trim().length > 0 ? u.name.trim() : nameFromEmail;
  const accountVertical = normalizeAccountVertical(u.accountVertical ?? u.account_vertical);
  const rawPreferredUiLocale = u.preferredUiLocale ?? u.preferred_ui_locale;
  const preferredUiLocale = typeof rawPreferredUiLocale === "string" && isSupportedUiLocale(rawPreferredUiLocale)
    ? rawPreferredUiLocale
    : null;

  return {
    id,
    email,
    name,
    emailVerified: u.emailVerified ?? true,
    onboardingComplete: u.onboardingComplete ?? true,
    accountVertical,
    preferredUiLocale,
    studios,
    studioRoles,
  };
}
